import { Component } from '@angular/core';
import { DatePipe, Location } from '@angular/common';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NotificationBar } from 'src/services/notification.service';
import { environment } from 'src/environment';
import { emsCompetition, emsMatchMeta, emsPlayer, emsTeam, emsMatch } from 'src/models/emsModels';
import { StageControlPanelComponent } from '../stage-control-panel/stage-control-panel.component';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { BreakpointObserver } from '@angular/cdk/layout';
import { NotesDialogComponent } from '../notes-dialog/notes-dialog.component';
import { VetoInfoDialogComponent } from '../veto-info-dialog/veto-info-dialog.component';
import { nationality } from 'src/models/countryModel';
import nationalitiesData from 'src/assets/nationality.json';
import { MatCalendarCellCssClasses } from '@angular/material/datepicker';

@Component({
  selector: 'app-stage',
  templateUrl: './stage.component.html',
  styleUrl: './stage.component.scss',
})
export class StageComponent {

  notificationBar: NotificationBar;
  workspaceID = "";
  competitionList = new Array<emsCompetition>();
  teamList = new Array<emsTeam>();
  playerList = new Array<emsPlayer>();
  isWorkspaceUser = false;
  isMobile = false;
  showMenu = true;
  selectedDate = "";
  placeholderTeamLogo = environment.placeholderTeamLogo;
  placeholderPersonImage = environment.placeholderPersonImage;
  matches = new Array<emsMatch>();
  activeTab = 0;
  nationalities = Array<nationality>();
  datesWithMatches = Array<string>();
  matchesDBRef: any;
  competitionsDBRef: any;
  teamsDBRef: any;
  playerDBRef: any;

  constructor(private title: Title, private router: Router, private location: Location, private snackBar: MatSnackBar, public dialog: MatDialog, private db: AngularFireDatabase, private fireAuth: AngularFireAuth, private breakpointObserver: BreakpointObserver) {
    this.notificationBar = new NotificationBar(snackBar);
    this.breakpointObserver.observe([`(max-width: 730px)`]).subscribe(result => {
      this.isMobile = result.matches;
    })
  }

  ngOnInit() { 
    this.workspaceID  = this.location.path().split("/")[1];
    this.title.setTitle("Virtual Stage - EsportsMS");
    var currentDate = new Date();
    var currentYear = currentDate.getFullYear();
    var currentMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
    var currentDay = String(currentDate.getDate()).padStart(2, '0');
    this.selectedDate = `${currentYear}-${currentMonth}-${currentDay}`;
    this.toggleMenu();
    this.loadCompetitions();
    this.loadTeams();
    this.loadPlayer();
    this.loadMatches();
    this.loadMatchCalendar();
    this.checkUserPermissions();
    this.loadCountryInfo();
  }

  loadCountryInfo() {
    this.nationalities = Object.entries(nationalitiesData).map(([code, name]) => ({ code: code.toLocaleLowerCase(), name })).sort((a, b) => a.name.localeCompare(b.name));
  }

  ngOnDestroy() {
    const toolbarHeader = document.querySelector('mat-toolbar') as HTMLElement;
    if (toolbarHeader) {
      toolbarHeader.setAttribute('style', `display: flex; visibility: visible`);
    }
    var siteContent = document.getElementsByClassName('site-content');
    siteContent.item(0).setAttribute('style', 'padding-top: 80px; padding-bottom: 75px;');
    var footerContent = document.getElementsByClassName('footer-content');
    footerContent.item(0).setAttribute('style', `display: block; visibility: visible`);
    if (this.matchesDBRef) {
      this.matchesDBRef.off('child_added');
      this.matchesDBRef.off('child_changed');
      this.matchesDBRef.off('child_removed');
    };
    if (this.competitionsDBRef) {
      this.competitionsDBRef.off('value');
    };
    if (this.teamsDBRef) {
      this.teamsDBRef.off('value');
    };
    if (this.playerDBRef) {
      this.playerDBRef.off('value');
    };
  }

  changeDate(event: any) {
    var datePipe = new DatePipe('en-US');
    this.selectedDate = datePipe.transform(event.value, 'yyyy-MM-dd');
    this.matches.forEach(match => {
      match.competition = new emsCompetition();
      match.teamA = new emsTeam();
      match.teamB = new emsTeam();
      match.teamAPlayer = new Array<emsPlayer>();
      match.teamBPlayer = new Array<emsPlayer>();
    })
    this.loadMatches();
  }

  async loadMatchCalendar() {
    var tempMatchesDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/stages/' + this.workspaceID + '/');
    await tempMatchesDBRef.once('value', x => {
      this.datesWithMatches = [];
      x.forEach(match => {  
        this.datesWithMatches.push(match.key);
      });
    });
  }
  
  setDateClass() {
    return (date: Date): MatCalendarCellCssClasses => {
      var highlightDate = this.datesWithMatches.map(strDate => new Date(strDate)).some(d => d.getDate() === date.getDate() && d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear());
      if (!highlightDate) {
        return '';
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateToCheck = new Date(date);
      dateToCheck.setHours(0, 0, 0, 0);
      if (dateToCheck < today) {
        return 'completed-matches';
      }
      if (dateToCheck > today) {
        return 'upcoming-matches';
      }
      return '';
    };
  }

  convertToEmsMatch(matchData: any) {
    var matchMeta = matchData.toJSON() as emsMatchMeta;
    var match = new emsMatch();
    match.uid = matchData.key!;
    match.matchMeta = matchMeta;
    match.title = (this.matches.length + 1) + ". Match";
    match.competition = this.competitionList.find(c => c.uid === matchMeta.competition) ?? new emsCompetition();
    match.teamA = this.teamList.find(t => t.uid === matchMeta.teamA) ?? new emsTeam();
    match.teamB = this.teamList.find(t => t.uid === matchMeta.teamB) ?? new emsTeam();
    match.teamAPlayer = this.playerList.filter(p => p.team === match.teamA.uid);
    match.teamBPlayer = this.playerList.filter(p => p.team === match.teamB.uid);
    return match;
  }

  async loadMatches() {
    this.matchesDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/stages/' + this.workspaceID + '/' + this.selectedDate);
    await this.matchesDBRef.once('value', x => {
      this.matches = new Array<emsMatch>();
      x.forEach(match => {
        this.matches.push(this.convertToEmsMatch(match));
      });
    });
    this.matchesDBRef.on('child_added', x => {
      if (this.matches.find(match => match.uid == x.key)) {
        return;
      } 
      this.matches.push(this.convertToEmsMatch(x));
    });
    this.matchesDBRef.on('child_changed', x => {
      var match = this.matches.find(match => match.uid == x.key);
      if (!match) { 
        return;
      }
      var newValues = this.convertToEmsMatch(x);
      match.matchMeta = newValues.matchMeta;
      match.competition = newValues.competition;
      match.teamA = newValues.teamA;
      match.teamB = newValues.teamB;
      match.teamAPlayer = newValues.teamAPlayer;
      match.teamBPlayer = newValues.teamBPlayer;
    });
    this.matchesDBRef.on('child_removed', x => {
      this.matches = this.matches.filter(match => match.uid != x.key);
    });
  }

  loadCompetitions() {
    var competitionsDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/competitions').orderByChild('workspace').equalTo(this.workspaceID);
    competitionsDBRef.on('value', x => {
      const rawData = x.toJSON(); // different because /player is an list, for object I would need /player/{teamID}
      this.competitionList = new Array<emsCompetition>();
      Object.keys(rawData).forEach(key => { 
        var competition = rawData[key] as emsCompetition;
        competition.uid = key;
        this.competitionList.push(competition);
        this.matches.forEach(match => {
          if (competition.uid == match.competition.uid) {
            match.competition = competition;
          }
        })
      });
    });
  }

  loadTeams() { 
    this.teamsDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/teams').orderByChild('workspace').equalTo(this.workspaceID);
    this.teamsDBRef.on('value', x => {
      const rawData = x.toJSON(); // different because /player is an list, for object I would need /player/{teamID}
      this.teamList = new Array<emsTeam>();
      Object.keys(rawData).forEach(key => { 
        var team = rawData[key] as emsTeam;
        team.uid = key;
        this.teamList.push(team);
        this.matches.forEach(match => {
          if (team.uid == match.teamA.uid) {
            match.teamA = team;
          }
          if (team.uid == match.teamB.uid) {
            match.teamB = team;
          }
        })
      });
    });
  }

  loadPlayer() {
    this.playerDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/player'); // FIND SOLUTION TO NOT LOAD ALL PLAYERS ALWAYS
    this.playerDBRef.on('value', x => {
      const rawData = x.toJSON(); // different because /player is an list, for object I would need /player/{teamID}
      this.playerList = new Array<emsPlayer>();
      this.matches.forEach(match => {
        match.teamAPlayer = new Array<emsPlayer>();
        match.teamBPlayer = new Array<emsPlayer>();
      })
      Object.keys(rawData).forEach(key => { 
        var player = rawData[key] as emsPlayer;
        player.uid = key;
        if (player.languages != undefined) {
          let langs = Object.values(player.languages);
          langs.sort((a, b) => {
            if (a === 'EN') return 1;
            if (b === 'EN') return -1;
            return 0;
          });
          player.languages = langs.join('/');
        }
        this.playerList.push(player);
        this.matches.forEach(match => {
          if (player.team == match.teamA.uid) {
            match.teamAPlayer.push(player);
          }
          if (player.team == match.teamB.uid) {
            match.teamBPlayer.push(player);
          }
        })
      });
    });
  }

  getPlayerFromTeamByPosition(teamPlayer: emsPlayer[], position: number) {
    return teamPlayer.find(player => player.position == position);
  }

  getPlayerNationalityName(emsPlayer?: emsPlayer) {
    if (!emsPlayer) return null;
    return this.nationalities.find(x => x.code == emsPlayer.nationality).name;
  }

  async checkUserPermissions() {
    var user = await (this.fireAuth.currentUser);
    if (user) {
      var tempUserWorkspaceDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/user/' + user.uid + '/workspaces/');
      tempUserWorkspaceDBRef.once('value').then( value => {
        value.forEach( userWorkspace => {
          if (userWorkspace.val() == true && userWorkspace.key == this.workspaceID) { 
            this.isWorkspaceUser = true;
            this.toggleMenu();
          }
        })
      });
    } else {
      this.isWorkspaceUser = false;
    }
  }

  openTeamNotes(team: emsTeam, teamPlayer: emsPlayer[]) {
    var dialogRef = this.dialog.open(NotesDialogComponent, {
      autoFocus: false,
      data: {
        teamName: team.name,
        teamNotes: team.notes,
        playerList: teamPlayer
        }
    });
  }

  toggleMenu() {
    if (this.showMenu == true) {
      var toolbarHeader = document.querySelector('mat-toolbar') as HTMLElement;
      if (toolbarHeader) {
        toolbarHeader.setAttribute('style', `display: none; visibility: hidden`);
      }
      var siteContent = document.getElementsByClassName('site-content');
      siteContent.item(0).setAttribute('style', 'padding-top: 10px; padding-bottom: 10px;');
      var footerContent = document.getElementsByClassName('footer-content');
      footerContent.item(0).setAttribute('style', `display: none; visibility: hidden`);
      this.showMenu = false;
    } 
    else {
      var toolbarHeader = document.querySelector('mat-toolbar') as HTMLElement;
      if (toolbarHeader) {
        toolbarHeader.setAttribute('style', `display: flex; visibility: visible`);
      }
      var siteContent = document.getElementsByClassName('site-content');
      siteContent.item(0).setAttribute('style', 'padding-top: 80px; padding-bottom: 75px;');
      var footerContent = document.getElementsByClassName('footer-content');
      footerContent.item(0).setAttribute('style', `display: block; visibility: visible`);
      this.showMenu = true;
    }
  }

  openControlPanel(matchIndex: number) {
    var dialogRef = this.dialog.open(StageControlPanelComponent, {
      data: {
        selectedDate: this.selectedDate,
        matchIndex: matchIndex
      }
    });
  }

  openVetoInfo(veto: string) {
    var dialogRef = this.dialog.open(VetoInfoDialogComponent, {
      data: {
        vetoUID: veto,
      }
    });
  }

}
